import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Search,
  Eye, 
  MessageSquare, 
  Bell, 
  BellRing,
  Settings, 
  Shield, 
  LogOut, 
  Menu, 
  X,
  Paintbrush,
  FileText,
  ClipboardList,
  Map,
  HelpCircle,
  CheckCircle,
  Clock,
  ChevronRight,
  Trash2,
  AlertTriangle,
  Info,
  Building2,
  School,
  Users,
  Lock,
  Database
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { 
  isPushSupported, 
  getPushStatus, 
  subscribeToPush, 
  sendTestNotification 
} from '../utils/pushNotifications';
import { TierBadge } from './UpgradePrompt';
import { TIER_LEVELS } from '../hooks/useTierAccess';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-track-6/artifacts/ylx5ssnx_Insite%20Logo%204.png';

// Main navigation - condensed
const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/compliance', label: 'Search', icon: Search },
  { path: '/sca-portal', label: 'SCA Portal', icon: School, restrictedTo: 'jh@insite360.com' },
  { path: '/map', label: 'Property Map', icon: Map },
  { path: '/watchlist', label: 'Monitoring', icon: Eye, subPaths: ['/watchlist', '/alerts', '/webhooks'], minTier: 'pro' },
  { path: '/teams', label: 'Teams', icon: Users, minTier: 'pro' },
  { path: '/chat', label: 'Help', icon: MessageSquare, minTier: 'pro' },
  { path: '/data-assistant', label: 'Insite AI', icon: Database, minTier: 'pro' },
  { path: '/reports', label: 'Reports', icon: FileText },
];

// Items moved to user dropdown
const userMenuItems = [
  { path: '/settings', label: 'Settings', icon: Settings },
  { path: '/help', label: 'Guide', icon: HelpCircle },
];

const adminItems = [
  { path: '/admin', label: 'Admin Panel', icon: Shield },
];

// Notification Dropdown Component
const NotificationDropdown = ({ 
  notifications, markAsRead, markAllAsRead, clearNotification, 
  getNotificationIcon, formatTime, navigate, setNotificationsOpen,
  pushEnabled, onEnablePush, pushLoading
}) => (
  <div className="max-h-[450px] overflow-hidden flex flex-col">
    {/* Header */}
    <div className="flex items-center justify-between p-3 border-b border-white/10">
      <h3 className="font-semibold text-white text-sm">Notifications</h3>
      {notifications.some(n => !n.read) && (
        <button 
          onClick={markAllAsRead}
          className="text-xs text-indigo-400 hover:text-indigo-300"
        >
          Mark all read
        </button>
      )}
    </div>
    
    {/* Push Notification Banner */}
    {isPushSupported() && !pushEnabled && (
      <div className="p-3 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border-b border-white/10">
        <div className="flex items-center gap-3">
          <BellRing className="w-5 h-5 text-indigo-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs text-white font-medium">Enable Desktop Notifications</p>
            <p className="text-[10px] text-zinc-400">Get instant alerts when violations are detected</p>
          </div>
          <button
            onClick={onEnablePush}
            disabled={pushLoading}
            className="px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {pushLoading ? '...' : 'Enable'}
          </button>
        </div>
      </div>
    )}
    
    {/* Push Enabled Badge */}
    {pushEnabled && (
      <div className="px-3 py-2 bg-green-500/10 border-b border-white/10 flex items-center gap-2">
        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
        <span className="text-[10px] text-green-400">Desktop notifications enabled</span>
      </div>
    )}
    
    {/* Notifications List */}
    <div className="overflow-y-auto flex-1">
      {notifications.length === 0 ? (
        <div className="p-6 text-center">
          <Bell className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">No notifications yet</p>
        </div>
      ) : (
        notifications.map(notif => (
          <div 
            key={notif.id}
            className={`p-3 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${
              !notif.read ? 'bg-indigo-500/5' : ''
            }`}
            onClick={() => {
              markAsRead(notif.id);
              if (notif.link) {
                navigate(notif.link);
                setNotificationsOpen(false);
              }
            }}
          >
            <div className="flex items-start gap-3">
              <div className={`p-1.5 rounded-lg ${!notif.read ? 'bg-white/10' : 'bg-white/5'}`}>
                {getNotificationIcon(notif.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-sm font-medium truncate ${!notif.read ? 'text-white' : 'text-zinc-400'}`}>
                    {notif.title}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearNotification(notif.id);
                    }}
                    className="p-1 text-zinc-600 hover:text-zinc-400 opacity-0 group-hover:opacity-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-xs text-zinc-500 truncate">{notif.message}</p>
                <p className="text-[10px] text-zinc-600 mt-1">{formatTime(notif.timestamp)}</p>
              </div>
              {!notif.read && (
                <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-1.5" />
              )}
            </div>
          </div>
        ))
      )}
    </div>
    
    {/* Footer */}
    <div className="p-2 border-t border-white/10">
      <Link 
        to="/alerts"
        onClick={() => setNotificationsOpen(false)}
        className="block w-full text-center text-xs text-indigo-400 hover:text-indigo-300 py-2"
      >
        View all alerts & settings
      </Link>
    </div>
  </div>
);

export const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  // Check push notification status on mount
  useEffect(() => {
    const checkPushStatus = async () => {
      try {
        const status = await getPushStatus();
        setPushEnabled(status.subscribed && status.permission === 'granted');
      } catch (e) {
        console.error('Error checking push status:', e);
      }
    };
    checkPushStatus();
  }, []);

  // Enable push notifications
  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      await subscribeToPush();
      setPushEnabled(true);
      // Send a test notification
      try {
        await sendTestNotification();
      } catch (e) {
        console.log('Test notification skipped:', e);
      }
    } catch (error) {
      console.error('Failed to enable push:', error);
      alert(error.message || 'Failed to enable notifications');
    } finally {
      setPushLoading(false);
    }
  };

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api().get('/notifications');
      if (res?.data) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      // Keep existing notifications on error (don't clear)
    }
  }, [api]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  // Mark notification as read
  const markAsRead = async (id) => {
    try {
      await api().post('/notifications/mark-read', { notification_ids: [String(id)] });
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api().post('/notifications/mark-read', { notification_ids: null });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Clear notification
  const clearNotification = async (id) => {
    const notif = notifications.find(n => n.id === id);
    try {
      await api().delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (!notif?.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to clear notification:', err);
    }
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'violation': return <AlertTriangle className="w-4 h-4 text-rose-400" />;
      case 'status': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'application': return <FileText className="w-4 h-4 text-indigo-400" />;
      case 'report': return <FileText className="w-4 h-4 text-amber-400" />;
      default: return <Bell className="w-4 h-4 text-zinc-400" />;
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Filter nav items based on user restrictions and tier
  const userTier = user?.tier || 'free';
  const userTierLevel = TIER_LEVELS[userTier] || 0;
  
  const filteredNavItems = navItems.filter(item => {
    if (item.restrictedTo) {
      return user?.email === item.restrictedTo;
    }
    return true;
  });
  
  const allNavItems = isAdmin ? [...filteredNavItems, ...adminItems] : filteredNavItems;

  return (
    <div className="dashboard-layout bg-zinc-950 min-h-screen">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#1A2433]/90 backdrop-blur-xl border-b border-[#1B3C6B]/30 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Pivotal Insite360" className="h-8 w-auto" />
        </Link>
        <div className="flex items-center gap-2">
          {/* Mobile Notification Bell */}
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="ghost"
                size="icon"
                className="relative text-[#8BA4C7] hover:text-white" 
                data-testid="mobile-notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose-500 rounded-full text-[10px] font-medium flex items-center justify-center text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 bg-zinc-900 border-white/10" align="end">
              <NotificationDropdown 
                notifications={notifications}
                markAsRead={markAsRead}
                markAllAsRead={markAllAsRead}
                clearNotification={clearNotification}
                getNotificationIcon={getNotificationIcon}
                formatTime={formatTime}
                navigate={navigate}
                setNotificationsOpen={() => {}}
                pushEnabled={pushEnabled}
                onEnablePush={handleEnablePush}
                pushLoading={pushLoading}
              />
            </PopoverContent>
          </Popover>
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-[#8BA4C7] hover:text-white"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-40 h-screen w-64 
        bg-zinc-900/50 backdrop-blur-xl border-r border-white/10
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#1B3C6B]/30">
            <Link to="/dashboard" className="flex items-center" data-testid="sidebar-logo">
              <img src={LOGO_URL} alt="Pivotal Insite360" className="h-28 w-auto" />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {allNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                (item.subPaths && item.subPaths.includes(location.pathname));
              const isLocked = !isAdmin && item.minTier && userTierLevel < (TIER_LEVELS[item.minTier] || 0);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-link ${isActive ? 'active' : ''} ${isLocked ? 'opacity-60' : ''}`}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="flex-1">{item.label}</span>
                  {isLocked && <Lock className="w-3.5 h-3.5 text-zinc-500" />}
                </Link>
              );
            })}
          </nav>

          {/* User Section with Expanded Menu */}
          <div className="p-4 border-t border-white/10">
            {/* Quick Links */}
            <div className="mb-4 space-y-1">
              {userMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const userTier = user?.tier || 'free';
                const hasAccess = !item.tier || userTier === item.tier || 
                  (TIER_LEVELS[userTier] || 0) >= (TIER_LEVELS[item.tier] || 0);
                
                if (item.tier && !hasAccess) return null;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-white/5 rounded-md transition-colors ${isActive ? 'text-white bg-white/5' : ''}`}
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* User Profile */}
            <div className="flex items-center gap-3 mb-3 pt-3 border-t border-white/5">
              <div className="w-10 h-10 rounded-full bg-indigo-600/30 flex items-center justify-center">
                <span className="text-indigo-400 font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <div className="mt-0.5"><TierBadge tier={user?.tier} /></div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-md transition-colors text-sm"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen lg:min-h-0 pt-16 lg:pt-0">
        {/* Desktop Header with Notifications */}
        <div className="hidden lg:flex items-center justify-end gap-4 px-8 py-4 border-b border-white/5">
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="ghost"
                size="icon"
                className="relative text-zinc-400 hover:text-white hover:bg-white/5" 
                data-testid="desktop-notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose-500 rounded-full text-[10px] font-medium flex items-center justify-center text-white animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 bg-zinc-900 border-white/10" align="end">
              <NotificationDropdown 
                notifications={notifications}
                markAsRead={markAsRead}
                markAllAsRead={markAllAsRead}
                clearNotification={clearNotification}
                getNotificationIcon={getNotificationIcon}
                formatTime={formatTime}
                navigate={navigate}
                setNotificationsOpen={() => {}}
                pushEnabled={pushEnabled}
                onEnablePush={handleEnablePush}
                pushLoading={pushLoading}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
